import React, { useState, useEffect } from 'react';
import { getServerSessionId } from '../../lib/serverSession';
import { ProfilePictureUpload } from '../ProfilePictureUpload';
import { filterAndRank } from '../../lib/searchRelevance';

interface FoundingShareholder {
  id: number;
  full_name: string;
  email: string;
  username: string;
  profile_picture_url: string | null;
  founder_description: string | null;
  show_founder_share_count: boolean;
  show_founder_affiliate_link: boolean;
  show_founder_phone: boolean;
  show_founder_description: boolean;
  founder_display_order: number | null;
  phone_number: string | null;
  total_shares: number;
  is_recognized_founder: boolean;
}

interface EditModalData {
  founder_description: string;
  show_founder_share_count: boolean;
  show_founder_affiliate_link: boolean;
  show_founder_phone: boolean;
  show_founder_description: boolean;
}

export const FoundingShareholdersManager: React.FC = () => {
  const [shareholders, setShareholders] = useState<FoundingShareholder[]>([]);
  const [allUsers, setAllUsers] = useState<FoundingShareholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingShareholder, setEditingShareholder] = useState<FoundingShareholder | null>(null);
  const [editFormData, setEditFormData] = useState<EditModalData | null>(null);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/founding-shareholders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_data' })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      const usersWithShares = result.users || [];
      setAllUsers(usersWithShares);

      // Filter recognized founders and sort by display order
      const recognizedFounders = usersWithShares
        .filter((user: any) => user.is_recognized_founder)
        .sort((a: any, b: any) => {
          if (a.founder_display_order !== null && b.founder_display_order !== null) {
            return a.founder_display_order - b.founder_display_order;
          }
          if (a.founder_display_order !== null) return -1;
          if (b.founder_display_order !== null) return 1;
          return b.total_shares - a.total_shares;
        });

      setShareholders(recognizedFounders);
    } catch (error: any) {
      console.error('Error loading data:', error);
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleFounderStatus = async (userId: number, currentStatus: boolean) => {
    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/founding-shareholders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'toggle_founder_status', data: { userId, currentStatus } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      await loadData();
    } catch (error: any) {
      console.error('Error toggling founder status:', error);
      alert('Error: ' + error.message);
    }
  };

  const openEditModal = (shareholder: FoundingShareholder) => {
    setEditingShareholder(shareholder);
    setEditFormData({
      founder_description: shareholder.founder_description || '',
      show_founder_share_count: shareholder.show_founder_share_count,
      show_founder_affiliate_link: shareholder.show_founder_affiliate_link,
      show_founder_phone: shareholder.show_founder_phone,
      show_founder_description: shareholder.show_founder_description
    });
  };

  const saveEdit = async () => {
    if (!editingShareholder || !editFormData) return;

    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/founding-shareholders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'save_edit', data: { userId: editingShareholder.id, editData: editFormData } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      setEditingShareholder(null);
      setEditFormData(null);
      await loadData();
      alert('✅ Founding shareholder updated successfully!');
    } catch (error: any) {
      console.error('Error saving edit:', error);
      alert('Error: ' + error.message);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === dropIndex) return;

    const newShareholders = [...shareholders];
    const draggedShareholder = newShareholders[draggedItem];
    newShareholders.splice(draggedItem, 1);
    newShareholders.splice(dropIndex, 0, draggedShareholder);

    // Update display order for all shareholders
    try {
      const updates = newShareholders.map((shareholder, index) => ({
        id: shareholder.id,
        founder_display_order: index + 1
      }));

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/founding-shareholders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'update_display_order', data: { updates } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      setShareholders(newShareholders);
      setDraggedItem(null);
    } catch (error: any) {
      console.error('Error updating order:', error);
      alert('Error updating order: ' + error.message);
    }
  };

  const filteredUsers = filterAndRank(
    allUsers.filter((u) => !u.is_recognized_founder),
    searchTerm,
    (u) => ({ primary: [u.full_name, u.email, u.username] }),
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">🏆 Founding Shareholders Management</h2>
          <p className="text-white mt-1">
            Manage recognized founding shareholders and their public profiles
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400 transition-colors"
        >
          ➕ Add Founding Shareholder
        </button>
      </div>

      {/* Current Founding Shareholders */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <h3 className="text-xl font-bold text-white mb-4">
          Current Founding Shareholders ({shareholders.length})
        </h3>
        <p className="text-white text-sm mb-4">
          💡 Drag and drop to reorder. The order here determines the display order on the public page.
        </p>

        {shareholders.length === 0 ? (
          <div className="text-center py-8 text-white">
            No founding shareholders yet. Click "Add Founding Shareholder" to get started.
          </div>
        ) : (
          <div className="space-y-3">
            {shareholders.map((shareholder, index) => (
              <div
                key={shareholder.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e)}
                onDrop={(e) => handleDrop(e, index)}
                className="bg-gray-700 rounded-lg p-4 cursor-move hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-white font-mono">#{index + 1}</span>
                    <ProfilePictureUpload
                      currentImageUrl={shareholder.profile_picture_url || undefined}
                      userId={shareholder.id}
                      onImageUpdate={loadData}
                      size="small"
                      editable={false}
                      userInitials={shareholder.full_name.charAt(0).toUpperCase()}
                      userName={shareholder.full_name}
                    />
                    <div>
                      <p className="text-white font-medium">{shareholder.full_name}</p>
                      <p className="text-white text-sm">{shareholder.email}</p>
                      <p className="text-gray-500 text-sm">
                        {shareholder.total_shares.toLocaleString()} shares
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditModal(shareholder)}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => toggleFounderStatus(shareholder.id, true)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    >
                      ❌ Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Add Founding Shareholder</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSearchTerm('');
                }}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <input
              type="text"
              placeholder="Search users by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg mb-4"
            />

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <p className="text-white text-center py-4">No users found</p>
              ) : (
                filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className="bg-gray-700 rounded-lg p-3 flex items-center justify-between hover:bg-gray-600"
                  >
                    <div className="flex items-center gap-3">
                      <ProfilePictureUpload
                        currentImageUrl={user.profile_picture_url || undefined}
                        userId={user.id}
                        onImageUpdate={() => {}}
                        size="small"
                        editable={false}
                        userInitials={user.full_name?.charAt(0).toUpperCase() || 'U'}
                        userName={user.full_name || user.username}
                      />
                      <div>
                        <p className="text-white font-medium">{user.full_name || user.username}</p>
                        <p className="text-white text-sm">{user.email}</p>
                        <p className="text-gray-500 text-sm">{user.total_shares.toLocaleString()} shares</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        toggleFounderStatus(user.id, false);
                        setShowAddModal(false);
                        setSearchTerm('');
                      }}
                      className="px-3 py-1 bg-yellow-500 text-black rounded hover:bg-yellow-400 text-sm font-medium"
                    >
                      ➕ Add
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingShareholder && editFormData && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">Edit Founding Shareholder</h3>
              <button
                onClick={() => {
                  setEditingShareholder(null);
                  setEditFormData(null);
                }}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* Profile Picture */}
              <div>
                <label className="block text-gray-300 font-medium mb-2">Profile Picture</label>
                <ProfilePictureUpload
                  currentImageUrl={editingShareholder.profile_picture_url || undefined}
                  userId={editingShareholder.id}
                  onImageUpdate={loadData}
                  size="large"
                  editable={true}
                  showSaveButton={true}
                  autoSave={false}
                  userInitials={editingShareholder.full_name.charAt(0).toUpperCase()}
                  userName={editingShareholder.full_name}
                />
              </div>

              {/* Read-only Info */}
              <div className="bg-gray-700 rounded-lg p-4 space-y-2">
                <div>
                  <p className="text-white text-sm">Full Name</p>
                  <p className="text-white font-medium">{editingShareholder.full_name}</p>
                </div>
                <div>
                  <p className="text-white text-sm">Email</p>
                  <p className="text-white">{editingShareholder.email}</p>
                </div>
                <div>
                  <p className="text-white text-sm">Total Shares</p>
                  <p className="text-white font-bold">{editingShareholder.total_shares.toLocaleString()}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-gray-300 font-medium mb-2">Bio / Description</label>
                <textarea
                  value={editFormData.founder_description}
                  onChange={(e) => setEditFormData({ ...editFormData, founder_description: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                  placeholder="Enter a bio or description for this founding shareholder..."
                />
              </div>

              {/* Visibility Checkboxes */}
              <div className="space-y-3">
                <p className="text-gray-300 font-medium">Public Visibility Settings</p>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFormData.show_founder_share_count}
                    onChange={(e) => setEditFormData({ ...editFormData, show_founder_share_count: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-white">Display share count publicly</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFormData.show_founder_affiliate_link}
                    onChange={(e) => setEditFormData({ ...editFormData, show_founder_affiliate_link: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-white">Display affiliate link publicly</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFormData.show_founder_phone}
                    onChange={(e) => setEditFormData({ ...editFormData, show_founder_phone: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-white">Display phone number publicly</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFormData.show_founder_description}
                    onChange={(e) => setEditFormData({ ...editFormData, show_founder_description: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-white">Display bio/description publicly</span>
                </label>
              </div>

              {/* Save Button */}
              <button
                onClick={saveEdit}
                className="w-full px-4 py-3 bg-yellow-500 text-black font-bold rounded-lg hover:bg-yellow-400 transition-colors"
              >
                💾 Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

